import { createReducer, on } from '@ngrx/store';
import {
  SaleHorseStudyBase,
  Study,
  StudyListItem,
} from 'src/app/models/Study/study.model';
import { MediaGroup } from 'src/app/models/media/media-group.model';
import { SaleHorseSetting } from 'src/app/models/sales/sale-horse-settings.model';
import { Status, StudyStatus } from 'src/app/models/status/status.model';
import { ReplaceStudyFileResponse, StudyFile } from '../../../models/Study/study-file.model';
import { LookupModel } from '../../../models/common/lookup.model';
import { ViewMode } from '../../enums/view-mode.emun';
import * as Actions from './studies.action';

export interface StudyState {
  studies: StudyListItem[];
  studiesLoaded: boolean;
  study: SaleHorseStudyBase;
  studyProfile: Study;
  studyPdfFiles: StudyFile[];
  sharedStudyFiles: StudyFile[];
  studyShareUID: string;
  studyLoaded: boolean;
  isLoading: boolean;
  statuses: Status[];
  mediaGroups: MediaGroup[];
  startedActivityLogId: number;
  isActivityEnded: boolean;
  studiesSearch: LookupModel[];
  studyFileMimeTypes: LookupModel[];
  studyFileDocTypes: LookupModel[];
  studiesDisplayData: LookupModel[];
  isStudiesSearchLoaded: boolean;
  isStudyMimeTypesLoaded: boolean;
  isStudyDocTypesLoaded: boolean;
  isStudySaleSubmitted: boolean;
  isStudyFileDocTypesUpdated: boolean;
  isStudyDescriptionUpdated: boolean;
  studySaleSubmittedErrors: any;
  studyStatusError: any;
  studyStatusSuccess: any;
  viewMode: ViewMode;
  saleSettings: SaleHorseSetting;
  cancelStudyAndDeleteFilesSuccess: boolean;
  cancelStudySubmissionSuccess: boolean;
  cancelStudyAndDeleteFilesError: any;
  cancelStudySubmissionError: any;
  setStudyActiveStatusSucess: boolean;
  setStudyActiveStatusError: any;
  studyListError: any;
  mergeSuccess: boolean | null;
  error: any;
  deleteStudyFileError: any;
  isStudyFileDeleted: boolean | null;
  shareSuccess: boolean | null;
  shareError: any;
  isStatusUpdated: boolean;
  completeBatchSuccess: boolean;
  errorCompleteBatch: any;
  isActivityPerform: boolean;
}

export const initialState: StudyState = {
  studies: [],
  studiesLoaded: false,
  study: null,
  studyProfile: null,
  studyLoaded: false,
  isLoading: false,
  statuses: [],
  mediaGroups: [],
  startedActivityLogId: 0,
  isActivityEnded: true,
  isStudiesSearchLoaded: false,
  isStudyMimeTypesLoaded: false,
  isStudyDocTypesLoaded: false,
  isStudySaleSubmitted: false,
  studySaleSubmittedErrors: null,
  isStudyFileDocTypesUpdated: false,
  isStudyDescriptionUpdated: false,
  studiesSearch: [],
  studyFileMimeTypes: [],
  studyFileDocTypes: [],
  sharedStudyFiles: [],
  studyShareUID: null,
  studiesDisplayData: [],
  studyStatusError: null,
  studyStatusSuccess: null,
  viewMode: ViewMode.DRAFT,
  studyPdfFiles: [],
  saleSettings: null,
  cancelStudyAndDeleteFilesSuccess: null,
  cancelStudySubmissionSuccess: null,
  cancelStudyAndDeleteFilesError: null,
  cancelStudySubmissionError: null,
  setStudyActiveStatusSucess: null,
  setStudyActiveStatusError: null,
  studyListError: null,
  mergeSuccess: null,
  error: null,
  deleteStudyFileError: null,
  isStudyFileDeleted: null,
  shareSuccess: null,
  shareError: null,
  isStatusUpdated: false,
  completeBatchSuccess: null,
  errorCompleteBatch: null,
  isActivityPerform: false
};

export const studyReducer = createReducer(
  initialState,

  on(Actions.GetSharedClinicalDataSuccess, (state, { studyShare }) => ({
    ...state,
    sharedStudyFiles: studyShare.studyFiles,
    studyShareUID: studyShare.studyShareUID,
  })),
  on(Actions.clearSharedStudyState, (state) => ({
    ...state,
    shareSuccess: null,
    shareError: null,
  })),
  on(Actions.ResetActivityState, (state) => ({
    ...state,
    isActivityPerform: false,
  })),
  on(Actions.ShareClinicalDataSuccess, (state, { success }) => ({
    ...state,
    shareSuccess: success,
  })),
  on(Actions.ShareClinicalDataFailure, (state, { error }) => ({
    ...state,
    shareError: error,
  })),
  on(Actions.OnCompleteBatch, (state) => ({
    ...state,
    completeBatchSuccess: null,
    errorCompleteBatch: null,
  })),
  on(Actions.OnCompleteBatchSuccess, (state, { response }) => ({
    ...state,
    completeBatchSuccess: response,
    isActivityPerform: true
  })),
  on(Actions.OnCompleteBatchFailure, (state, { error }) => ({
    ...state,
    errorCompleteBatch: error,
    isActivityPerform: true
  })),
  on(Actions.mergeStudies, (state) => ({
    ...state,
    mergeSuccess: null,
    error: null,
  })),
  on(Actions.mergeStudiesSuccess, (state, { success }) => ({
    ...state,
    mergeSuccess: success,
  })),
  on(Actions.mergeStudiesFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(Actions.clearDeleteFileError, (state) => ({
    ...state,
    deleteStudyFileError: null,
  })),
  on(Actions.clearMergeSuccess, (state) => ({
    ...state,
    mergeSuccess: null,
  })),

  on(Actions.clearStudySubmitToSale, (state) => ({
    ...state,
    isStudySaleSubmitted: null,
  })),
  on(Actions.clearCancelStudyAndDeleteFilesError, (state) => ({
    ...state,
    cancelStudyAndDeleteFilesError: null,
  })),
  on(Actions.ClearSetStudyActiveStatusFiles, (state) => ({
    ...state,
    setStudyActiveStatusError: null,
    setStudyActiveStatusSucess: null,
  })),
  on(Actions.clearStudiesLoaded, (state) => ({
    ...state,
    studiesLoaded: false,
  })),
  on(Actions.StudySubmitToSale, (state, { studySale }) => {
    return {
      ...state,
      isStudySaleSubmitted: false,
    };
  }),
  on(Actions.CancelStudyFilesSuccess, (state, { result }) => {
    return {
      ...state,
      cancelStudyAndDeleteFilesError: null,
      cancelStudyAndDeleteFilesSuccess: result,
      isActivityPerform: true,
      study: {
        ...state.study,
        studyFiles: [],
      },
    };
  }),
  on(Actions.ClearCancelStudyAndDeleteFilesSuccess, (state) => {
    return {
      ...state,
      cancelStudyAndDeleteFilesSuccess: null,
    };
  }),
  on(Actions.CancelStudyFilesFailure, (state, { err }) => {
    return {
      ...state,
      cancelStudyAndDeleteFilesSuccess: false,
      cancelStudyAndDeleteFilesError: err,
    };
  }),
  on(Actions.CancelStudySubmissionSuccess, (state, { result }) => {
    return {
      ...state,
      cancelStudySubmissionSuccess: result,
      isActivityPerform: true,
      cancelStudySubmissionError: null,
    };
  }),
  on(Actions.CancelStudySubmissionFailure, (state, { err }) => {
    return {
      ...state,
      cancelStudySubmissionSuccess: false,
      cancelStudySubmissionError: err,
    };
  }),
  on(Actions.SetStudyActiveStatusFilesSuccess, (state, { result }) => {
    return {
      ...state,
      setStudyActiveStatusError: null,
      setStudyActiveStatusSucess: result,
      isActivityPerform: true,
      study: {
        ...state.study,
        studyFiles: [],
      },
    };
  }),
  on(Actions.SetStudyActiveStatusFilesFailure, (state, { err }) => {
    return {
      ...state,
      setStudyActiveStatusSucess: false,
      setStudyActiveStatusError: err,
    };
  }),
  on(Actions.StudySubmitToSaleSuccess, (state, { response }) => {
    return {
      ...state,
      isStudySaleSubmitted: response,
    };
  }),
  on(Actions.StudySubmitToSaleFailure, (state, { err }) => {
    return {
      ...state,
      studySaleSubmittedErrors: err,
    };
  }),
  on(Actions.UpdateDescriptionSuccess, (state, { response }) => {
    return {
      ...state,
      isStudyDescriptionUpdated: response,
    };
  }),
  on(
    Actions.UpdateDocTypeSuccess,
    (state, { studyFileId, docTypeId, docTypeName }) => {
      const studyFiles = state.study.studyFiles.map((sf) => {
        if (sf.studyFileId === studyFileId) {
          const studyFile = { ...sf };
          studyFile.documentTypeId = docTypeId;
          studyFile.documentTypeName = docTypeName;

          return studyFile;
        } else {
          return sf;
        }
      });
      return {
        ...state,
        study: {
          ...state.study,
          studyFiles,
        },
      };
    }
  ),
  on(Actions.GetStudiesSuccess, (state, { studies }) => {
    return {
      ...state,
      studies: studies,
      studiesLoaded: true,
      viewMode: ViewMode.DRAFT,
    };
  }),
  on(Actions.GetStudiesFailure, (state, { err }) => {
    return {
      ...state,
      studies: [],
      studyListError: err,
      studiesLoaded: true,
      viewMode: ViewMode.DRAFT,
    };
  }),
  on(Actions.GetStudiesManifestSuccess, (state, { manifest }) => {
    return {
      ...state,
    };
  }),
  on(Actions.GetStudiesManifestFailure, (state, { err }) => {
    return {
      ...state,
    };
  }),
  on(Actions.StudiesSearch, (state, { lotNumber }) => {
    return {
      ...state,
      studiesSearch: [],
      isStudiesSearchLoaded: false,
      studiesDisplayData: [],
    };
  }),
  on(Actions.StudiesSearchSuccess, (state, { response }) => {
    return {
      ...state,
      studiesSearch: response,
      isStudiesSearchLoaded: true,
      studiesDisplayData: response.slice(0, 50),
    };
  }),
  on(Actions.GetStudyFilesMimeTypes, (state, {}) => {
    return {
      ...state,
      studyFileMimeTypes: [],
      isStudyMimeTypesLoaded: false,
    };
  }),
  on(Actions.GetStudyFilesMimeTypesSuccess, (state, { mimeTypes }) => {
    return {
      ...state,
      studyFileMimeTypes: mimeTypes,
      isStudyMimeTypesLoaded: true,
    };
  }),

  on(Actions.GetStudyFilesDocTypes, (state, {}) => {
    return {
      ...state,
      studyFileMimeTypes: [],
      isStudyDocTypesLoaded: false,
    };
  }),
  on(Actions.GetStudyFilesDocTypesSuccess, (state, { docTypes }) => {
    return {
      ...state,
      studyFileDocTypes: docTypes,
      isStudyDocTypesLoaded: true,
    };
  }),
  on(Actions.StudiesSearchFilter, (state, { query }) => {
    const filterData = state.studiesSearch?.filter((saleHorse) => {
      const hasLotNumber = saleHorse?.name?.toString().includes(query);
      return !!hasLotNumber;
    });

    return { ...state, studiesDisplayData: filterData.slice(0, 50) };
  }),
  on(Actions.GetStudyById, (state) => {
    return {
      ...state,
      isLoading: true,
      isStudyFileDeleted: null,
    };
  }),
  on(
    Actions.GetStudyByIdSuccess,
    (state, { studyProfile, studyFiles, studyStatus }) => {
      let study: SaleHorseStudyBase = new SaleHorseStudyBase();
      study.studyFiles = studyFiles;
      study.saleId = studyProfile.saleId;
      study.horseId = studyProfile.horseId;
      return {
        ...state,
        studyProfile: {
          ...studyProfile,
          studyFiles,
          studyStatus,
        },
        study: study,
        studyLoaded: true,
        isLoading: false,
      };
    }
  ),
  on(Actions.GetStudyFiles, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(Actions.GetStudyFilesSuccess, (state, { studyFiles, saleId, horseId, isSalesCompany }) => {
    let study: SaleHorseStudyBase = new SaleHorseStudyBase();
    study.studyFiles = studyFiles;
    study.saleId = saleId;
    study.horseId = horseId;
    study.isSalesCompany = isSalesCompany;
    return {
      ...state,
      study: study,
      isLoading: false,
    };
  }),
  on(Actions.GetStudyPdfFilesByStudyId, (state, { saleId, horseId }) => {
    return {
      ...state,
      studyPdfFiles: [],
    };
  }),
  on(Actions.GetStudyPdfFilesByStudyIdSuccess, (state, { studyFiles }) => {
    return {
      ...state,
      studyPdfFiles: studyFiles,
    };
  }),
  on(Actions.GetStudyFilesBySaleHorse, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),

  on(
    Actions.GetStudyFilesWithSaleHorseSuccess,
    (state, { studyFiles, saleHorse, isSalesCompany }) => {
      let study: SaleHorseStudyBase = new SaleHorseStudyBase();
      study.studyStatus = new StudyStatus();
      study.studyFiles = studyFiles;
      study.saleId = saleHorse.saleId;
      study.horseId = saleHorse.horseId;
      study.saleHorse = saleHorse;
      study.isSalesCompany = isSalesCompany;
      return {
        ...state,
        study: study,
        isLoading: false,
      };
    }
  ),

  on(Actions.GetStatusesSuccess, (state, { statuses }) => {
    return {
      ...state,
      statuses,
    };
  }),

  on(Actions.GetMediaGroupsSuccess, (state, { mediaGroups }) => {
    return {
      ...state,
      mediaGroups,
    };
  }),

  on(Actions.CreateStudyFileSuccess, (state, { studyFile }) => {
    const studyFiles = [...state.study.studyFiles];
    studyFiles.push(studyFile);
    return {
      ...state,
      study: {
        ...state.study,
        studyFiles,
      },
    };
  }),
  on(Actions.UpdateSelectedTabSuccess, (state, { submissionStatusId }) => {
    if (submissionStatusId === state?.study?.saleHorse?.submissionStatusId) {
      // Do nothing
      return state;
    }
    return {
      ...state,
      study: {
        ...state.study,
        saleHorse: {
          ...state.study.saleHorse,
          submissionStatusId: submissionStatusId,
        },
      },
    };
  }),
  on(Actions.UpdateStudyFileSuccess, (state, { studyFile }) => {
    const studyFiles = [...state.study.studyFiles];
    if (studyFile.hasOwnProperty('item1')) {
      let indexOf = studyFiles.findIndex(
        (sf) => sf.studyFileId === studyFile.item1.studyFileId
      );
      studyFiles.splice(indexOf, 1, studyFile.item1);
      if (studyFile.item2.studyFileId) {
        indexOf = studyFiles.findIndex(
          (sf) => sf.studyFileId === studyFile.item2.studyFileId
        );
        studyFiles.splice(indexOf, 1, studyFile.item2);
      }
    
    } else  {

      const indexOf = studyFiles.findIndex(
        (sf) => sf.studyFileId === studyFile.studyFileId
      );
      studyFiles.splice(indexOf, 1, studyFile);
    }
    return {
      ...state,
      study: {
        ...state.study,
        studyFiles,
      },
    };
  }),
  on(Actions.DeleteStudyFileFailure, (state, { err }) => {
    return {
      ...state,
      isStudyFileDeleted: false,
      deleteStudyFileError: err,
    };
  }),
  on(Actions.DeleteStudyFileSuccess, (state, { studyFileId }) => {
    const studyFiles = [
      ...state.study.studyFiles.filter((sf) => sf.studyFileId !== studyFileId),
    ];

    return {
      ...state,
      isStudyFileDeleted: true,
      isActivityPerform: true,
      study: {
        ...state.study,
        studyFiles,
      },
    };
  }),
  on(Actions.UpdateStudyFileStatuses, (state) => {
    return {
      ...state,
      studyStatusSuccess: null,
      isStatusUpdated: false,
      isLoading: true,
    };
  }),
  on(
    Actions.UpdateStudyFileStatusesSuccess,
    (state, { updateStatusResponse }) => {
      const studyFiles = updateStatusResponse.studyFiles.map((studyFile) => {
        const updatedStatus = updateStatusResponse.studyFileIds.includes(
          studyFile.studyFileId
        )
          ? updateStatusResponse.statusId
          : studyFile.studyStatusId;

        const updatedStatusMessage = updateStatusResponse.studyFileIds.includes(
          studyFile.studyFileId
        )
          ? updateStatusResponse.statusMessage
          : studyFile.statusMessage;
        return {
          ...studyFile,
          studyStatusId: updatedStatus,
          statusMessage: updatedStatusMessage,
        };
      });

      return {
        ...state,
        studyStatusSuccess: updateStatusResponse,
        isStatusUpdated: true,
        isActivityPerform: true, 
        study: {
          ...state.study,
          studyFiles,
        },
        isLoading: false,
      };
    }
  ),
  on(Actions.UpdateStudyFileStatusesFailure, (state, { err }) => {
    return {
      ...state,
      studyStatusError: err,
      isStatusUpdated: false,
      study: {
        ...state.study,
      },
    };
  }),
  on(Actions.UpdateStudyStatusSuccess, (state, { updateStatusResponse }) => {
    const studyFiles = state.study.studyFiles.map((studyFile) => {
      const updatedStatus = updateStatusResponse.studyFileIds.includes(
        studyFile.studyFileId
      )
        ? updateStatusResponse.statusId
        : studyFile.studyStatusId;

      return {
        ...studyFile,
        studyStatusId: updatedStatus,
      };
    });

    return {
      ...state,
      studyStatusSuccess: updateStatusResponse,
      isStatusUpdated: true,
      study: {
        ...state.study,
        studyFiles,
        studyStatus: {
          ...state.study.studyStatus,
          statusId: updateStatusResponse.statusId,
          statusName: updateStatusResponse.statusName,
        },
      },
    };
  }),
  on(Actions.UpdateStudyStatusFailure, (state, { err }) => {
    return {
      ...state,
      studyStatusError: err,
      study: {
        ...state.study,
      },
    };
  }),
  on(Actions.ClearStudyStatusResponseState, (state) => {
    return {
      ...state,
      studyStatusError: null,
      studyStatusSuccess: null,
      study: {
        ...state.study,
      },
    };
  }),
  on(Actions.ClearStudyStatusState, (state) => {
    return {
      ...state,
      studyStatusError: null,
      studyStatusSuccess: null,
      isStatusUpdated: false,
      study: {
        ...state.study,
      },
    };
  }),
  on(Actions.GetSaleHorseStudiesSuccess, (state, { saleHorseStudy }) => {
    return {
      ...state,
      study: saleHorseStudy,
    };
  }),

  on(Actions.ResetData, (state) => {
    return {
      ...state,
      study: null,
    };
  }),
  on(Actions.StartActivity, (state) => {
    return {
      ...state,
      startedActivityLogId: 0,
      isActivityEnded: false,
    };
  }),
  on(Actions.StartActivitySuccess, (state, { activityLogId }): StudyState => {
    return {
      ...state,
      startedActivityLogId: activityLogId,
      isActivityEnded: false,
    };
  }),
  on(Actions.EndActivity, (state) => {
    return {
      ...state,
      startedActivityLogId: 0,
      isActivityEnded: true,
    };
  }),
  on(Actions.EndActivitySuccess, (state, { response }): StudyState => {
    return { ...state, startedActivityLogId: 0, isActivityEnded: true };
  }),
  on(Actions.SetViewMode, (state, { viewMode }): StudyState => {
    return { ...state, viewMode };
  }),
  on(Actions.GetSaleSettingsSuccess, (state, { saleSettings }) => {
    return {
      ...state,
      saleSettings: saleSettings,
      saleHorsesLoaded: false,
    };
  })
);
